import * as R from 'ramda';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import useSearchParams from '../../../../../hooks/useSearchParams';
import PATHS from '../../../../../routes/routes.const';
import {
  useGetCatalog,
  useGetCatalogSections,
} from '../../../../../services/modules/catalogs/catalogs.queries';
import catalogModalStore from '../../../../../stores/CatalogModal/CatalogModalStore';
import {
  CLASSIFICATIONS_PREFIX,
  getClassificationFilterIds,
} from '../../../../classifications/classification-filter.utils';
import { useApplicationContext } from '../../../../context/ApplicationContext/ApplicationContext';
import { useAuthContext } from '../../../../login/AuthContext';
import useSharedEntities from '../../../../shared-entities/hooks/useSharedEntities';
import useCanManageBrandCatalogs from '../../../hooks/useCanManageBrandCatalogs';
import { CATALOG_FILTERS_KEYS } from '../../catalogFilters.constants';
import { useCatalogGroupParams } from '../../v2/hooks/useCatalogGroupParams';
import { useCatalogListContextParams } from '../../v2/hooks/useCatalogListContextParams';
import { useCatalogUrlFilters } from '../../v2/hooks/useCatalogUrlFilters';
import useCatalogLoadError from './useCatalogLoadError';
import useCatalogProductsFilters from './useCatalogProductsFilters';
import {
  buildCatalogBreadcrumbItems,
  buildCatalogViewConfig,
  resolveCatalogReadMode,
} from './useCatalogProductsScreen.utils';

interface UseCatalogProductsScreenParams {
  catalogId: string;
  isOfflineMode?: boolean;
  canPersist?: boolean;
}

const useCatalogProductsScreen = ({
  catalogId,
  isOfflineMode = false,
  canPersist = false,
}: UseCatalogProductsScreenParams) => {
  const history = useHistory();
  const { searchParams } = useSearchParams();
  const { filters: urlFilters, customerIds } = useCatalogUrlFilters();
  const { isSeller } = useApplicationContext();
  const { loggedin } = useAuthContext();
  const { isPublicAccess } = useSharedEntities({});
  const canEditCatalogs = useCanManageBrandCatalogs();

  const [isShareOpen, setIsShareOpen] = useState(false);

  const filters = useCatalogProductsFilters({ isOfflineMode });

  const contextParams = useCatalogListContextParams();
  const { groupParams } = useCatalogGroupParams({ catalogId, canPersist });

  const catalogQuery = useGetCatalog({ id: catalogId, isOfflineMode }, { enabled: !!catalogId });
  const sectionsQuery = useGetCatalogSections(
    { id: catalogId, filters: urlFilters, customerIds, contextParams, groupParams, isOfflineMode },
    { enabled: !!catalogId },
  );

  const catalogListPath = isOfflineMode ? PATHS.offline.catalogs.path : PATHS.main.catalogs.path;

  useCatalogLoadError({
    isError: catalogQuery.isError,
    error: catalogQuery.error,
    isRedirectEnabled: !isPublicAccess,
    redirectPath: catalogListPath,
  });

  const catalog = catalogQuery.data;
  const catalogConfig = buildCatalogViewConfig(catalog);
  const isCatalogSettled = catalogQuery.isSuccess || catalogQuery.isError;
  const sections = sectionsQuery.data?.sections ?? [];

  const canExport = (isSeller || canEditCatalogs) && !isOfflineMode;
  const readMode = resolveCatalogReadMode({
    isPublicAccess,
    canEditCatalogs,
    isOfflineMode,
  });
  const totalItems = sections.reduce((sum, section) => sum + section.itemsCount, 0);
  const isDynamic = catalog?.isDynamic ?? false;
  const showShare = loggedin && !isOfflineMode;
  const showDetailsToggle = !isPublicAccess && canEditCatalogs;
  const showCatalogsBreadcrumbNav = loggedin;

  const goToCatalogList = () => {
    history.push(catalogListPath);
  };

  const breadcrumbItems = buildCatalogBreadcrumbItems({
    showCatalogsBreadcrumbNav,
    catalogName: catalog?.name,
    goToCatalogList,
  });

  const getActiveFilters = (): Record<string, string | string[]> | undefined => {
    const active = new Map<string, string | string[]>();

    Object.values(CATALOG_FILTERS_KEYS).forEach((paramKey) => {
      if (paramKey === CATALOG_FILTERS_KEYS.onlySuggestedProducts) {
        return;
      }
      const value = searchParams[paramKey];
      if (value !== undefined && value !== null && value !== '') {
        active.set(paramKey, value);
      }
    });

    const onlySuggestedProducts = searchParams[CATALOG_FILTERS_KEYS.onlySuggestedProducts];
    if (onlySuggestedProducts) {
      active.set(CATALOG_FILTERS_KEYS.onlySuggestedProducts, onlySuggestedProducts);
      active.set('suggestedProductCustomerIds', R.uniq(customerIds));
    }

    const { classificationIds } = getClassificationFilterIds(searchParams);
    if (classificationIds.length > 0) {
      active.set(CATALOG_FILTERS_KEYS.classifications, classificationIds);
    }

    return active.size > 0 ? Object.fromEntries(active) : undefined;
  };

  const getInitialClassificationTypeId = (): string | undefined => {
    const keys = Object.keys(searchParams).filter((key) => key.startsWith(CLASSIFICATIONS_PREFIX));
    if (keys.length !== 1) {
      return undefined;
    }
    return keys[0].slice(CLASSIFICATIONS_PREFIX.length);
  };

  const handleExport = () => {
    if (!catalog) {
      return;
    }

    catalogModalStore.openModal({
      catalog,
      activeFilters: getActiveFilters(),
      initialClassificationTypeId: getInitialClassificationTypeId(),
    });
  };

  const handleShare = () => {
    setIsShareOpen(true);
  };

  const closeShare = () => {
    setIsShareOpen(false);
  };

  return {
    catalog,
    catalogConfig,
    isCatalogSettled,
    sections,
    canExport,
    canEditCatalogs,
    readMode,
    totalItems,
    isDynamic,
    showShare,
    showDetailsToggle,
    breadcrumbItems,
    filters,
    handleExport,
    handleShare,
    isShareOpen,
    closeShare,
  };
};

export default useCatalogProductsScreen;
